import { useState } from "react";
import {
    StarIcon,
    EnvelopeIcon,
    PlusIcon,
    XMarkIcon,
    EyeIcon,
    BuildingOfficeIcon,
    MapPinIcon,
    BriefcaseIcon,
    GlobeAltIcon
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";
import { IMatch } from "../../interfaces/match";
import { updateMatch, convertMatchToProspect } from "../../interfaces/matches";
import { useNavigate } from "react-router-dom";
import { BiEuro, BiUser } from "react-icons/bi";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";

interface MatchCardProps {
    match: IMatch;
    onUpdate: () => void;
}

export default function MatchCard({ match, onUpdate }: MatchCardProps) {
    const navigate = useNavigate();
    const [icpRating, setIcpRating] = useState<number>(match.icpRating || 0);
    const [showNotes, setShowNotes] = useState(false);
    const [notes, setNotes] = useState(match.icpRatingNotes || "");
    const [loading, setLoading] = useState(false);

    const handleRating = async (rating: number) => {
        try {
            setIcpRating(rating);
            await updateMatch(match._id, { icpRating: rating, status: 'reviewed' });
            onUpdate();
        } catch (err) {
            console.error('Failed to update rating:', err);
        }
    };

    const handleSaveNotes = async () => {
        try {
            setLoading(true);
            await updateMatch(match._id, { icpRatingNotes: notes });
            setShowNotes(false);
            onUpdate();
        } catch (err) {
            console.error('Failed to save notes:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddToKanban = async () => {
        if (!window.confirm(`Add "${match.prospectData.name || match.prospectData.company}" to your kanban?`)) {
            return;
        }

        try {
            setLoading(true);
            const _result = await convertMatchToProspect(match._id);
            onUpdate();
        } catch (err: any) {
            console.error('Failed to add to kanban:', err);
            alert(err.response?.data?.message || 'Failed to add to kanban');
        } finally {
            setLoading(false);
        }
    };

    const _handleContact = () => {
        // Navigate to compose email with pre-filled prospect data
        navigate(`/compose-email/new`, {
            state: {
                prospectData: match.prospectData,
                matchId: match._id
            }
        });
    };

    const handleDismiss = async () => {
        if (!window.confirm('Dismiss this match?')) {
            return;
        }

        try {
            setLoading(true);
            await updateMatch(match._id, { status: 'dismissed' });
            onUpdate();
        } catch (err) {
            console.error('Failed to dismiss match:', err);
        } finally {
            setLoading(false);
        }
    };

    const data = match.prospectData;
    const displayName = data.name || `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unknown';
    const isConverted = match.status === 'added_to_kanban';

    return (
        <div className={`rounded-2xl border p-4 transition-colors duration-150 ${
            isConverted ? 'border-success bg-success-subtle' : 'border-border bg-surface hover:border-border-strong'
        }`}>
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    {data.profilePicture ? (
                        <img
                            src={data.profilePicture}
                            alt={displayName}
                            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                        />
                    ) : (
                        <div className="w-10 h-10 rounded-full bg-surface flex items-center justify-center text-ink border-ink border-2 text-sm font-semibold flex-shrink-0">
                            {displayName.charAt(0).toUpperCase()}
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-ink truncate">{displayName}</h3>
                        {data.title && (
                            <p className="text-[11px] text-ink-muted flex items-center gap-1 truncate">
                                <BriefcaseIcon className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{data.title}</span>
                            </p>
                        )}
                        {data.company && (
                            <p className="text-[11px] text-ink-muted flex items-center gap-1 truncate">
                                <BuildingOfficeIcon className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{data.company}</span>
                            </p>
                        )}
                    </div>
                </div>

                {/* Match Score Badge */}
                {match.matchScore !== undefined && (
                    <Badge tone="info" className="flex-shrink-0">
                        {match.matchScore}%
                    </Badge>
                )}
            </div>

            {/* Details */}
            <div className="space-y-1.5 mb-3 text-[11px] text-ink-muted">
                <div className="flex items-center gap-2">
                {data.location && (
                    <p className="flex items-center gap-1 truncate">
                        <MapPinIcon className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{data.location}</span>
                    </p>
                )}
                {data.location && (
                    <p className="flex items-center gap-1 truncate">
                        <BiEuro className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">
                            {typeof data.capital === "number"
                                ? data.capital.toLocaleString("en-US").replace(/,/g, " ")
                                : ""}
                        </span>
                    </p>
                )}
                {data.employees && (
                    <p className="flex items-center gap-1 truncate">
                        <BiUser className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{data.employees}</span>
                    </p>
                )}
                </div>
                {/* <div className="flex flex-wrap gap-1.5 items-center">
                    {data.industry && (
                        <span className="bg-gray-50 text-gray-700 border border-gray-200 px-2 py-0.5 rounded-sm inline-block">
                            {data.industry}
                        </span>
                    )}
                    {data.employees !== undefined && (
                        <span className="text-gray-500">
                            {data.employees} employees
                        </span>
                    )}
                </div> */}
            </div>

            {/* Contact Info */}
            <div className="flex flex-wrap gap-2 mb-3">
                {data.email && (
                    <a
                        href={`mailto:${data.email}`}
                        className="text-[11px] text-ink-muted hover:text-ink flex items-center gap-1 transition-colors duration-150"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <EnvelopeIcon className="w-3 h-3" />
                        Email
                    </a>
                )}
                {data.linkedinUrl && (
                    <a
                        href={data.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] text-ink-muted hover:text-ink flex items-center gap-1 transition-colors duration-150"
                        onClick={(e) => e.stopPropagation()}
                    >
                        LinkedIn
                    </a>
                )}
                {data.website && (
                    <a
                        href={data.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] text-ink-muted hover:text-ink flex items-center gap-1 transition-colors duration-150"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <GlobeAltIcon className="w-3 h-3" />
                        Website
                    </a>
                )}
            </div>

            {/* ICP Rating */}
            <div className="mb-3 p-3 bg-surface-sunken border border-border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-bold text-ink">ICP Fit Rating</span>
                    <button
                        onClick={() => setShowNotes(!showNotes)}
                        className="text-[11px] text-ink-muted hover:text-ink transition-colors duration-150"
                    >
                        {showNotes ? 'Hide' : 'Add'} notes
                    </button>
                </div>
                <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                            key={rating}
                            onClick={() => handleRating(rating)}
                            className="transition-all hover:scale-110"
                            disabled={loading}
                        >
                            {rating <= icpRating ? (
                                <StarSolidIcon className="w-4 h-4 text-warning" />
                            ) : (
                                <StarIcon className="w-4 h-4 text-ink-faint hover:text-warning" />
                            )}
                        </button>
                    ))}
                </div>
                {icpRating > 0 && (
                    <p className="text-[11px] text-ink-muted mt-1.5">{icpRating}/5</p>
                )}

                {showNotes && (
                    <div className="mt-2">
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Add notes about this match..."
                            className="w-full text-[11px] border border-border rounded-lg bg-surface p-2 text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent transition-colors duration-150"
                            rows={3}
                        />
                        <Button
                            onClick={handleSaveNotes}
                            disabled={loading}
                            size="sm"
                            className="mt-1.5"
                        >
                            Save Notes
                        </Button>
                    </div>
                )}
            </div>

            {/* Status Badge */}
            {isConverted && (
                <Badge tone="success" className="mb-3">
                    ✓ Added to Kanban
                </Badge>
            )}

            {/* Actions */}
            <div className="flex gap-2">
                {!isConverted && (
                    <>
                        <Button
                            onClick={handleAddToKanban}
                            disabled={loading}
                            variant="primary"
                            size="sm"
                            className="flex-1"
                        >
                            <PlusIcon className="w-3.5 h-3.5" />
                            Add to Kanban
                        </Button>
                        <Button
                            onClick={handleDismiss}
                            disabled={loading}
                            variant="secondary"
                            size="sm"
                        >
                            <XMarkIcon className="w-3.5 h-3.5" />
                        </Button>
                    </>
                )}
                {isConverted && match.prospectId && (
                    <Button
                        onClick={() => navigate('/kanban')}
                        variant="primary"
                        size="sm"
                        className="flex-1"
                    >
                        <EyeIcon className="w-3.5 h-3.5" />
                        View in Kanban
                    </Button>
                )}
            </div>
        </div>
    );
}
